import { useMsal } from '@azure/msal-react';
import { useParams } from "react-router-dom";
import UserApi from '../../api/user/user-api-factory';
import { useEffect, useState, useMemo, createContext } from "react";
import { Link } from "react-router-dom";
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import LoadingAnimation from "../../components/loading/loading";
import { CompanyAPI } from '../../api/company/company-api-factory';
import { LandLordsTab } from './landLords-tab';
import { ManageUsers } from '../user/manage-users';
import { CompanyDetailsTab } from '../../components/company/company-details-tab';
import { arrayHasMoreThanNItems } from '../../utils/arrayUtils';
import { useUserProfile } from '../../app';
import { XeroIntegrationTab } from '../../components/company/xero-integration-tab';

export const CompanyContext = createContext();

export const CompanyDetail = ({ company }) => {

    const getTabIndexFromName = tabName => {

        switch (tabName) {
            default:
            case "details":
                return 0;
            case "landlords":
                return 1;
            case "payment methods":
                return 2;
            case "billing history":
                return 3;
            case "users":
                return 4;
            case "contacts":
                return 5;
            case "notes":
                return 6;
            case "documents":
                return 7;
            case "xero":
                return 7;
        }
    };

    const { instance, inProgress, accounts } = useMsal();
    const profile = useUserProfile();
    const userApi = useMemo(() => new UserApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const mapApiRoleToDisplayRole = apiRole => {
        const roleMap = {
            tenant_Manager: 'Tenant.Manager',
            user_Basic: 'User.Basic',
            billing_Admin: 'Billing.Admin',
            application_Developer: 'Application.Developer',
        };
        return roleMap[apiRole] || apiRole;
    };

    const authorized = useMemo(() => {
        const primaryAccount = accounts && accounts[0];
        const aadRoles = primaryAccount?.idTokenClaims?.roles || [];
        const apiRoles = profile?.systemProfile?.apiRoles || [];
        const mappedApiRoles = apiRoles.length ? apiRoles.map(mapApiRoleToDisplayRole) : [];
        const roles = mappedApiRoles.length ? mappedApiRoles : aadRoles;

        return arrayHasMoreThanNItems(accounts, 0) &&
            roles && roles.length &&
            (userApi.hasRole(roles, 'Application.Developer') ||
                userApi.hasRole(roles, 'Tenant.Manager'));
    }, [accounts, userApi, profile]);

    const { id, tab } = useParams();

    const [state, setState] = useState(structuredClone(company));
    const [originalState, setOriginalState] = useState(structuredClone(company));
    const [fetchCompanyInProgress, setFetchCompanyInProgress] = useState(false);
    const [shouldFetchCompany, setShouldFetchCompany] = useState(false);
    const [tabIndex, setTabIndex] = useState(getTabIndexFromName(tab ?? 'details'));
    const [showLandlordDelete, setShowLandlordDelete] = useState(false);

    const api = useMemo(() => new CompanyAPI(instance, inProgress, accounts, true), [instance, inProgress, accounts]); // TODO: REVERT - testing localStorage

    useEffect(() => {
        if (!company && authorized) {
            setShouldFetchCompany(true);
        }
    }, [company, authorized]);

    useEffect(() => {
        async function fetchData() {
            if (authorized && shouldFetchCompany && !fetchCompanyInProgress) {
                setFetchCompanyInProgress(true);
                try {
                    const data = await api.get(id);
                    setState(data);

                } catch (error) {
                    console.log("An error occurred getting data from the Company Data API.", error);
                    setState(null);
                }
                finally {
                    setShouldFetchCompany(false);
                    setFetchCompanyInProgress(false);
                }
            }
        }
        fetchData();
    }, [shouldFetchCompany, fetchCompanyInProgress, id, instance, inProgress, accounts, authorized, api]);

    return authorized ? (
        <CompanyContext.Provider value={{
            saveCompany: async newCompanyData => { const result = await api.updateCompany(newCompanyData, newCompanyData.id); setState(result); setOriginalState(result); return result; }
        }}>
            <ol className="breadcrumb float-xl-end">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item"><Link to="/company/search">Company Search</Link></li>
                <li className="breadcrumb-item active">Details</li>
            </ol>

            {!fetchCompanyInProgress && state &&
                <>
                    <h1 className="page-header">Details | <small>Manage company details for <strong>{state.name}</strong></small></h1>

                    <Tabs selectedIndex={tabIndex} onSelect={index => setTabIndex(index)}>
                        <TabList className="nav nav-pills mb-2" >
                            <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 0 ? 'active' : ''} `}>Details</div></Tab>
                            <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 1 ? 'active' : ''} `}>Landlords</div></Tab>
                            <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 2 ? 'active' : ''} `}>Payment Methods</div></Tab>
                            <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 3 ? 'active' : ''} `}>Billing History</div></Tab>
                            <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 4 ? 'active' : ''} `}>Users</div></Tab>
                            <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 5 ? 'active' : ''} `}>Contacts</div></Tab>
                            <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 6 ? 'active' : ''} `}>Notes</div></Tab>
                            <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 7 ? 'active' : ''} `}>Documents</div></Tab>
                            <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 8 ? 'active' : ''} `}>Xero Integration</div></Tab>
                        </TabList>

                        <TabPanel>
                            <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                <CompanyDetailsTab company={state} saveCompany={() => console.log("Save")} />
                            </div>
                        </TabPanel>
                        {LandLordsTab(showLandlordDelete, state)}
                        <TabPanel>
                            <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                Payment Methods Tab
                            </div>
                        </TabPanel>
                        <TabPanel>
                            <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                Billing History Tab
                            </div>
                        </TabPanel>
                        <TabPanel>
                            <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                <ManageUsers companyName={state.name} />
                            </div>
                        </TabPanel>
                        <TabPanel>
                            <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                Contacts Tab
                            </div>
                        </TabPanel>
                        <TabPanel>
                            <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                Notes Tab
                            </div>
                        </TabPanel>
                        <TabPanel>
                            <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                Documents Tab
                            </div>
                        </TabPanel>
                        <TabPanel>
                            <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                <XeroIntegrationTab company={state} />
                            </div>
                        </TabPanel>
                    </Tabs>
                </>
            }
            {fetchCompanyInProgress && <LoadingAnimation />}
        </CompanyContext.Provider>
    )
        :
        <>Nothing to show... you may not have permission to view this data</>
}